import { Link } from 'react-router';
import type { PoolShipping } from '../../lib/api/operatorConsole';
import { formatAmount, formatUsdCents } from './formatMoney';
import { StatusPill } from './StatusPill';

interface Props {
  shipping: PoolShipping;
}

const cell = 'px-4 py-3 text-sm text-ui-text';
const mutedCell = 'px-4 py-3 text-sm text-ui-text-secondary';

function addressLine(shipping: PoolShipping): string {
  const address = shipping.address;
  if (!address) return '—';

  return [address.name, address.city, address.country].filter(Boolean).join(', ') || '—';
}

// A charged delivery without a tax line paid no tax: that is a zero, not an
// unknown. Only a delivery with no charge snapshot has nothing to show.
function salesTaxLine(shipping: PoolShipping): string {
  if (!shipping.fees) return '—';

  return formatAmount(shipping.fees.sales_tax ?? 0, shipping.currency);
}

export function ShippingRow({ shipping }: Props) {
  return (
    <tr className="border-b border-ui-border hover:bg-ui-bg">
      <td className={cell}>
        <Link to={`/shippings/${shipping.id}`} className="underline">
          #{shipping.id}
        </Link>
      </td>
      <td className={cell}>
        <div className="font-mono">{shipping.operator_account_id ?? '—'}</div>
        <div className="text-[11px] text-ui-text-secondary">user #{shipping.user_id}</div>
      </td>
      <td className={mutedCell}>
        {shipping.status_updated_at ? new Date(shipping.status_updated_at).toLocaleString() : '—'}
      </td>
      <td className={cell}>
        <StatusPill shipping={shipping} />
      </td>
      <td className={`${mutedCell} capitalize`}>{shipping.direction}</td>
      <td className={mutedCell}>{shipping.source.name}</td>
      <td className={cell}>
        <div className="capitalize">{shipping.shipping_type.replace(/_/g, ' ')}</div>
        {shipping.ship_my_cards_nickname && (
          <div className="text-[11px] text-ui-text-secondary">
            {shipping.ship_my_cards_nickname}
          </div>
        )}
      </td>
      <td className={mutedCell}>{addressLine(shipping)}</td>
      <td className={mutedCell}>{shipping.address?.zipcode || '—'}</td>
      {/* The delivery's total is its fee total (shipping + handling + tax), so
          the list shows it once; the split is on the detail page and in the CSV. */}
      <td className={`${cell} font-mono`}>
        {formatAmount(shipping.total_amount, shipping.currency)}
      </td>
      <td className={`${mutedCell} font-mono`}>{salesTaxLine(shipping)}</td>
      <td className={`${cell} font-mono`}>
        {/* A backend that predates the field omits it: unknown, not $NaN. */}
        {formatUsdCents(shipping.locked_fmv_usd_cents ?? null)}
      </td>
      <td className={mutedCell}>{shipping.cards_count}</td>
    </tr>
  );
}
